import { Button, Tooltip, theme } from 'antd';
import type { GlobalToken } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import type { MovimientoClienteMensual } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { DashboardCard } from '../../../components/DashboardCard';
import { CardEyebrow } from '../../../components/CardEyebrow';
import { monthAxisLabel } from '../../../lib/chart-utils';
import { useT } from '../../../locales';
import { buildMovimientoCard } from '../dashboard-captions';

// Same fixed height as the card it replaces, so the four first-level cards
// stay aligned.
const CARD_HEIGHT = 160;
// With the prose + legend collapsed into the info tooltip, the grouped bars
// own the body: the chart is the hero of the card.
const CHART_HEIGHT = 100;

interface MovimientoClientesChartProps {
  movimientoClientes: MovimientoClienteMensual[];
}

interface MovimientoDatum {
  mes: string;
  serie: string;
  value: number;
}

interface TooltipDatum {
  serie?: string;
  value?: number;
}

interface TooltipItem {
  data?: TooltipDatum;
  serie?: string;
  name?: string;
  value?: number;
}

interface TooltipContext {
  title: string;
  items: TooltipItem[];
}

function finiteValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function tooltipSeries(item: TooltipItem): string {
  return item.data?.serie ?? item.serie ?? item.name ?? '';
}

function tooltipValue(item: TooltipItem): number {
  return finiteValue(item.data?.value ?? item.value);
}

/**
 * One bar-tooltip row for a movement count: color dot + series label + the raw
 * count. Counts are integers, so no currency formatting (unlike
 * `tooltipRowHTML` in chart-utils).
 */
function movementRowHTML(name: string, value: number, color: string, token: GlobalToken): string {
  return `
  <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin:4px 0;">
    <span style="display:inline-flex;align-items:center;gap:6px;color:${token.colorTextSecondary};font-size:12px;">
      <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};"></span>${name}
    </span>
    <strong style="color:${token.colorText};font-variant-numeric:tabular-nums;font-size:12px;">${value}</strong>
  </div>`;
}

/**
 * "Movimiento de Clientes" card: grouped 3-series columns (altas / bajas /
 * reactivaciones) over the same 6-month window the old "Altas de Clientes"
 * card used, read from the shared `movimientoClientes` DTO.
 *
 * The bars are the hero. The explanatory prose is collapsed into a small info
 * tooltip in the header; the color legend is G2's auto-legend styled with
 * Kanagawa tokens (same idiom as SerieMensualChart).
 */
export function MovimientoClientesChart({
  movimientoClientes,
}: MovimientoClientesChartProps) {
  const t = useT();
  const { token } = theme.useToken();
  const card = buildMovimientoCard(t);

  const altasLabel = t('dashboard.movimientoClientesAltas');
  const bajasLabel = t('dashboard.movimientoClientesBajas');
  const reactivacionesLabel = t('dashboard.movimientoClientesReactivaciones');

  // Semantically distinct colors per series: green enrolls, red cancels,
  // blue reactivations.
  const altasColor = token.colorSuccess;
  const bajasColor = token.colorError;
  const reactivacionesColor = token.colorInfo;

  const hasData = movimientoClientes.some(
    (m) => m.altas > 0 || m.bajas > 0 || m.reactivaciones > 0,
  );

  const data: MovimientoDatum[] = movimientoClientes.flatMap((m) => {
    const mes = monthAxisLabel(m.mes);
    return [
      { mes, serie: altasLabel, value: m.altas },
      { mes, serie: bajasLabel, value: m.bajas },
      { mes, serie: reactivacionesLabel, value: m.reactivaciones },
    ];
  });

  // Bar tooltip: month + each series label + its raw count. Same idiom as the
  // other @ant-design/charts in this repo (title fn + interaction.render).
  const movimientoTooltipRender = (_event: unknown, context: TooltipContext) => {
    const bySerie = new Map(context.items.map((item) => [tooltipSeries(item), tooltipValue(item)]));
    const altas = bySerie.get(altasLabel) ?? 0;
    const bajas = bySerie.get(bajasLabel) ?? 0;
    const reactivaciones = bySerie.get(reactivacionesLabel) ?? 0;
    return `
      <div style="min-width:180px;margin:-12px;padding:12px;background:${token.colorBgElevated};border-radius:4px;">
        <div style="font-weight:600;font-size:12px;color:${token.colorText};margin-bottom:6px;">${context.title}</div>
        ${movementRowHTML(altasLabel, altas, altasColor, token)}
        ${movementRowHTML(bajasLabel, bajas, bajasColor, token)}
        ${movementRowHTML(reactivacionesLabel, reactivaciones, reactivacionesColor, token)}
      </div>`;
  };

  // All the descriptive prose in one place: the series description, carried
  // by the info tooltip so the bars stay the visual hero of the card. The
  // color legend is G2's own auto-legend (styled with Kanagawa tokens, same
  // idiom as SerieMensualChart), so it is not duplicated here.
  const tooltipContent = (
    <div data-testid="movimiento-tooltip" style={{ maxWidth: 280 }}>
      <div
        data-testid="movimiento-tooltip-desc"
        style={{ fontSize: 12, lineHeight: 1.4, color: token.colorText }}
      >
        {card.description}
      </div>
    </div>
  );

  return (
    <DashboardCard style={{ height: CARD_HEIGHT }} bodyStyle={{ padding: '12px 18px 0' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <CardEyebrow label={card.title} color={token.colorPrimary} />
        <Tooltip
          title={tooltipContent}
          placement="bottomRight"
          styles={{
            container: {
              background: token.colorBgElevated,
              color: token.colorText,
            },
          }}
        >
          <Button
            type="text"
            size="small"
            shape="circle"
            data-testid="movimiento-info"
            aria-label={t('dashboard.movimientoClientesInfoLabel')}
            icon={<InfoCircleOutlined aria-hidden="true" />}
          />
        </Tooltip>
      </div>
      {!hasData ? (
        <EmptyState
          compact
          title={t('dashboard.movimientoClientesSinDatos')}
          description={t('dashboard.movimientoClientesSinDatosDesc')}
        />
      ) : (
        <div
          role="img"
          aria-label={t('dashboard.movimientoClientesTrendAria')}
          style={{ minWidth: 0 }}
        >
          <Column
            data={data}
            xField="mes"
            yField="value"
            colorField="serie"
            group
            color={[altasColor, bajasColor, reactivacionesColor]}
            scale={{
              color: {
                domain: [altasLabel, bajasLabel, reactivacionesLabel],
                range: [altasColor, bajasColor, reactivacionesColor],
              },
            }}
            tooltip={{
              title: (d: { mes: string }) => d.mes,
            }}
            interaction={{
              tooltip: { render: movimientoTooltipRender },
            }}
            height={CHART_HEIGHT}
            axis={false}
            legend={{
              color: {
                position: 'top',
                layout: { justifyContent: 'center' },
                itemLabelFill: token.colorText,
              },
            }}
          />
        </div>
      )}
    </DashboardCard>
  );
}
